import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import {
  deleteV1CompaniesCompanyIdProjectsId,
  ModelError_Response,
  ProjectState,
  ProjectStateEvent,
  StateError_Response,
} from '@treadinc/horizon-api-spec';
import { AxiosError, isAxiosError } from 'axios';
import { t } from 'i18next';
import { upperFirst } from 'lodash-es';
import { observer } from 'mobx-react-lite';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Virtuoso } from 'react-virtuoso';

import ConfirmationDialog from '~components/Dialog/ConfirmationDialog';
import DividerWithText from '~components/DividerWithText/DividerWithText';
import {
  LoadingSpinner,
  SmallButton,
} from '~components/Order/ordersDispatchStyledComponents';
import StatusTabs from '~components/Tabs/StatusTabs';
import Tabs from '~components/Tabs/Tabs';
import { FeatureFlags } from '~constants/featureFlags';
import OutboundEmailProgressContext from '~contexts/OutboundEmailProgressContext';
import useIsCompanySuspended from '~hooks/useIsCompanySuspended/useIsCompanySuspended';
import { OutboundEmailEntry } from '~hooks/useOutboundEmails/sendOutboundEmails';
import useProjectOutboundEmails from '~hooks/useOutboundEmails/useProjectOutboundEmails';
import { Project } from '~hooks/useProjects/models';
import { transitionProject } from '~hooks/useProjects/transitionProject';
import useQuotePdf from '~hooks/useProjects/useQuotePdf';
import useServerQuotePdf from '~hooks/useProjects/useServerQuotePdf';
import connection from '~services/connectionModule';
import { useStores } from '~store';
import { alert, AlertTypes } from '~types/AlertTypes';
import { useFeatureFlag } from '~utils/hooks/useFeatureFlag';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import EmailQuoteDialog from './components/EmailQuoteDialog';
import ProjectsFilters from './components/Filters/ProjectsFilters';
import Search from './components/Filters/Search';
import ProjectDialog from './components/ProjectFormDialog/ProjectDialog';
import ProjectsListCard from './components/ProjectsListCard/ProjectsListCard';
import { GridContainer, GridRow } from './components/styled';
import { ProjectImportAction } from './ProjectImportAction';
import ProjectsQueryContext, {
  ProjectsQueryContextProvider,
} from './ProjectsQueryContext';
import {
  getDefaultProjectStateForView,
  getInitialProjectState,
  getInitialProjectsView,
  getProjectStateLabel,
  getViewForState,
  isProjectStateInView,
  isValidProjectState,
  isValidProjectsView,
  PROJECT_STATE_TABS_BY_VIEW,
  ProjectsStorageKeys,
  ProjectsURLParams,
  ProjectsView,
} from './projectTabs';

type ProjectCreationMode = 'project' | 'quote';

type ProjectFormDialogState = {
  creationMode?: ProjectCreationMode;
  isCopyAndEdit?: boolean;
  isOpen: boolean;
  project?: Project;
};

type EmailQuoteDialogState = {
  isOpen: boolean;
  project?: Project;
};

type DeleteProjectDialogState = {
  isDeleting?: boolean;
  isOpen: boolean;
  project?: Project;
};

function ProjectsContainer() {
  const { userStore } = useStores();
  const companyId = getEffectiveUserCompanyId(userStore);
  const [searchParams] = useSearchParams();

  const initialView = useMemo(
    () => getInitialProjectsView(searchParams),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
  const initialState = useMemo(
    () => getInitialProjectState(searchParams, initialView),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  return (
    <ProjectsQueryContextProvider
      companyId={companyId}
      initialFilters={{ states: [initialState] }}
    >
      <Projects initialState={initialState} initialView={initialView} />
    </ProjectsQueryContextProvider>
  );
}

interface ProjectsProps {
  initialState: ProjectState;
  initialView: ProjectsView;
}

function Projects({ initialState, initialView }: ProjectsProps) {
  const {
    applyFilters,
    clearQueryData,
    data,
    fetchMore,
    fetchReason,
    hasMore,
    refresh,
    removeStoredProject,
    updateStoredProject,
  } = useContext(ProjectsQueryContext);

  const [searchParams, setSearchParams] = useSearchParams();
  const [view, setView] = useState<ProjectsView>(initialView);
  const [stateTab, setStateTab] = useState<ProjectState>(initialState);

  const persistViewAndStateTab = useCallback(
    (nextView: ProjectsView | undefined, nextStateTab: ProjectState) => {
      if (nextView) {
        localStorage.setItem(ProjectsStorageKeys.VIEW_PARAM, nextView);
      }
      localStorage.setItem(ProjectsStorageKeys.TAB_PARAM, nextStateTab);
      setSearchParams((params) => {
        const updated = new URLSearchParams(params);
        if (nextView) {
          updated.set(ProjectsURLParams.VIEW, nextView);
        }
        updated.set(ProjectsURLParams.TAB, nextStateTab);

        return updated;
      });
    },
    [setSearchParams],
  );

  useEffect(() => {
    const urlViewParam = searchParams.get(ProjectsURLParams.VIEW);
    const urlTabParam = searchParams.get(ProjectsURLParams.TAB);

    const urlView = isValidProjectsView(urlViewParam) ? urlViewParam : null;
    const urlState = isValidProjectState(urlTabParam) ? urlTabParam : null;

    if (!urlView && !urlState) {
      return;
    }

    const resolvedView: ProjectsView = urlView ?? getViewForState(urlState!);
    const resolvedState: ProjectState =
      urlState && isProjectStateInView(resolvedView, urlState)
        ? urlState
        : getDefaultProjectStateForView(resolvedView);

    if (resolvedView !== view) {
      setView(resolvedView);
      localStorage.setItem(ProjectsStorageKeys.VIEW_PARAM, resolvedView);
    }
    if (resolvedState !== stateTab) {
      setStateTab(resolvedState);
      localStorage.setItem(ProjectsStorageKeys.TAB_PARAM, resolvedState);
      applyFilters({ states: [resolvedState] });
    }
  }, [applyFilters, searchParams, stateTab, view]);

  const handleViewChange = useCallback(
    (next: ProjectsView) => {
      if (next === view) {
        return;
      }
      const defaultState = getDefaultProjectStateForView(next);

      setView(next);
      setStateTab(defaultState);
      persistViewAndStateTab(next, defaultState);
      applyFilters({ states: [defaultState] });
    },
    [applyFilters, persistViewAndStateTab, view],
  );

  const handleStateTabChange = useCallback(
    (next: ProjectState) => {
      if (next === stateTab) {
        return;
      }

      setStateTab(next);
      persistViewAndStateTab(undefined, next);
      applyFilters({ states: [next] });
    },
    [applyFilters, persistViewAndStateTab, stateTab],
  );

  const { userStore, toasterStore } = useStores();
  const isCompanySuspended = useIsCompanySuspended();
  const companyId = getEffectiveUserCompanyId(userStore);
  const { canEditProject, canCreateProject, canDeleteProject } =
    userStore.getPermissions();
  const isServerQuotePdfEnabled = useFeatureFlag({
    featureFlagKey: FeatureFlags.serverQuotePdf,
  });
  const generateBrowserQuotePdf = useQuotePdf();
  const generateServerQuotePdf = useServerQuotePdf();
  const generateQuotePdf = isServerQuotePdfEnabled
    ? generateServerQuotePdf
    : generateBrowserQuotePdf;
  const [generatingQuotePdfIds, setGeneratingQuotePdfIds] = useState<Set<string>>(
    new Set(),
  );

  const handleViewQuotePdfClick = useCallback(
    async (project: Project) => {
      setGeneratingQuotePdfIds((prev) => new Set(prev).add(project.id));
      try {
        await generateQuotePdf(project);
      } finally {
        setGeneratingQuotePdfIds((prev) => {
          const next = new Set(prev);
          next.delete(project.id);
          return next;
        });
      }
    },
    [generateQuotePdf],
  );

  const { enqueueOutboundEmail } = useContext(OutboundEmailProgressContext);
  const { sendProjectEmail } = useProjectOutboundEmails();
  const [emailQuoteDialog, setEmailQuoteDialog] = useState<EmailQuoteDialogState>({
    isOpen: false,
  });

  const handleEmailQuotePdfClick = useCallback((project: Project) => {
    setEmailQuoteDialog({ isOpen: true, project });
  }, []);

  const handleEmailQuoteClose = useCallback(() => {
    setEmailQuoteDialog({ isOpen: false });
  }, []);

  const handleEmailQuoteSend = useCallback(
    async (emails: OutboundEmailEntry[]) => {
      const { operationId, recipientCount } = await sendProjectEmail({
        companyId,
        emails,
      });

      enqueueOutboundEmail(operationId, recipientCount);
    },
    [companyId, enqueueOutboundEmail, sendProjectEmail],
  );

  const [projectFormDialog, setProjectFormDialog] = useState<ProjectFormDialogState>({
    isOpen: false,
  });
  const [deleteProjectDialogState, setDeleteProjectDialogState] =
    useState<DeleteProjectDialogState>({ isOpen: false });
  const [pendingTransition, setPendingTransition] = useState<{
    projectId: string;
    event: ProjectStateEvent;
  } | null>(null);

  const deleteProject = useCallback(async () => {
    if (!deleteProjectDialogState.project || deleteProjectDialogState.isDeleting) {
      return;
    }

    try {
      setDeleteProjectDialogState((state) => ({ ...state, isDeleting: true }));
      await deleteV1CompaniesCompanyIdProjectsId({
        path: { 'company-id': companyId, id: deleteProjectDialogState.project.id },
      });
      removeStoredProject(deleteProjectDialogState.project);
      toasterStore.push(alert(t('sales_v2.project_deleted'), AlertTypes.success));
    } catch (error) {
      if (isAxiosError(error) && error.response?.status === 422) {
        const e = error as AxiosError<ModelError_Response>;
        const message = upperFirst(e.response?.data.error.errors?.[0]?.message);

        if (message) {
          toasterStore.push(alert(message, AlertTypes.error));
        } else {
          connection.handleRequestError(
            error,
            t('error_messages.projects.failed_to_delete'),
          );
        }
      } else {
        connection.handleRequestError(
          error,
          t('error_messages.projects.failed_to_delete'),
        );
      }
    } finally {
      setDeleteProjectDialogState((state) => ({
        ...state,
        isDeleting: false,
        isOpen: false,
      }));
    }
  }, [companyId, deleteProjectDialogState, removeStoredProject]);

  const dispatchStateEvent = useCallback(
    async (project: Project, event: ProjectStateEvent) => {
      if (pendingTransition) {
        return;
      }

      setPendingTransition({ projectId: project.id, event });

      try {
        const updatedProject = await transitionProject({
          companyId,
          event,
          projectId: project.id,
        });

        updateStoredProject(updatedProject);
        toasterStore.push(alert(t(`project.transitioned.${event}`), AlertTypes.success));
      } catch (error) {
        if (isAxiosError(error) && error.response?.status === 409) {
          const stateError = error as AxiosError<StateError_Response>;
          const message = upperFirst(
            stateError.response?.data.error.errors?.[0]?.message,
          );

          toasterStore.push(
            alert(
              message || t('error_messages.projects.invalid_transition'),
              AlertTypes.error,
            ),
          );

          return;
        }

        connection.handleRequestError(
          error,
          t('error_messages.projects.failed_to_transition'),
        );
      } finally {
        setPendingTransition(null);
      }
    },
    [companyId, pendingTransition, toasterStore, updateStoredProject],
  );

  const handleCreateProjectClick = useCallback(() => {
    setProjectFormDialog({ creationMode: 'project', isOpen: true });
  }, []);

  const handleCreateQuoteClick = useCallback(() => {
    setProjectFormDialog({ creationMode: 'quote', isOpen: true });
  }, []);

  const handleEditProjectClick = useCallback((project: Project) => {
    setProjectFormDialog({ isOpen: true, project });
  }, []);

  const handleCopyAndEditProjectClick = useCallback((project: Project) => {
    const isQuoteState =
      project.state && getViewForState(project.state) === ProjectsView.QUOTES;

    setProjectFormDialog({
      creationMode: isQuoteState ? 'quote' : 'project',
      isCopyAndEdit: true,
      isOpen: true,
      project,
    });
  }, []);

  const handleDeleteProjectClick = useCallback((project: Project) => {
    setDeleteProjectDialogState({ isOpen: true, project });
  }, []);

  const handleToggleProjectActiveStateClick = useCallback(
    async (project: Project) => {
      const event = project.isActive
        ? ProjectStateEvent.DEACTIVATE
        : ProjectStateEvent.ACTIVATE;

      await dispatchStateEvent(project, event);
    },
    [dispatchStateEvent],
  );

  const handleLoadMore = useCallback(() => {
    if (hasMore && !fetchReason) {
      fetchMore();
    }
  }, [hasMore, fetchReason, fetchMore]);

  useEffect(() => {
    return () => {
      refresh();
    };
  }, [companyId, refresh]);

  useEffect(() => {
    return () => {
      clearQueryData();
    };
  }, [clearQueryData]);

  const projects = data ?? [];
  const projectsCount = projects.length;
  const itemCount = projectsCount + 1;

  const renderProjectItem = useCallback(
    (index: number) => {
      const isLastItem = index === itemCount - 1;
      const project = projects[index];

      if (isLastItem) {
        return (
          <Box sx={{ py: 1.5, px: 6 }}>
            {fetchReason ? (
              <LoadingSpinner isVisible />
            ) : (
              <DividerWithText
                text={
                  projectsCount ? t('sales_v2.all_projects_loaded') : t('common.no_data')
                }
              />
            )}
          </Box>
        );
      }

      return (
        <Box sx={{ pt: 1.5, pb: index === projectsCount - 1 ? 1.5 : 0, px: 6 }}>
          {project ? (
            <ProjectsListCard
              isGeneratingPdf={generatingQuotePdfIds.has(project.id)}
              onCopyAndEditProjectClick={
                canCreateProject ? handleCopyAndEditProjectClick : undefined
              }
              onDeleteProjectClick={
                canDeleteProject ? handleDeleteProjectClick : undefined
              }
              onEditProjectClick={canEditProject ? handleEditProjectClick : undefined}
              onEmailQuotePdfClick={handleEmailQuotePdfClick}
              onSelectStateEvent={canEditProject ? dispatchStateEvent : undefined}
              onToggleProjectActiveStateClick={
                canEditProject ? handleToggleProjectActiveStateClick : undefined
              }
              onViewQuotePdfClick={handleViewQuotePdfClick}
              pendingStateEvent={
                pendingTransition?.projectId === project.id
                  ? pendingTransition.event
                  : null
              }
              project={project}
            />
          ) : null}
        </Box>
      );
    },
    [
      canCreateProject,
      canDeleteProject,
      canEditProject,
      dispatchStateEvent,
      fetchReason,
      generatingQuotePdfIds,
      handleCopyAndEditProjectClick,
      handleDeleteProjectClick,
      handleEditProjectClick,
      handleEmailQuotePdfClick,
      handleToggleProjectActiveStateClick,
      handleViewQuotePdfClick,
      itemCount,
      pendingTransition,
      projects,
      projectsCount,
    ],
  );

  return (
    <>
      <GridContainer>
        <GridRow sx={{ borderTop: 'none' }}>
          <Box
            sx={{
              alignItems: 'center',
              display: 'flex',
              justifyContent: 'space-between',
            }}
          >
            <Tabs
              onChange={handleViewChange}
              selected={view}
              sx={{ minWidth: '240px' }}
              tabs={[
                {
                  dataUi: 'projects-view-tab-quotes',
                  label: t('project.view.quotes'),
                  value: ProjectsView.QUOTES,
                },
                {
                  dataUi: 'projects-view-tab-projects',
                  label: t('project.view.projects'),
                  value: ProjectsView.PROJECTS,
                },
              ]}
            />

            <Box sx={{ alignItems: 'center', display: 'flex', gap: 1 }}>
              <ProjectImportAction onImportComplete={refresh} />

              {canCreateProject && (
                <>
                  <Tooltip
                    title={
                      isCompanySuspended
                        ? t('billing.contact_billing_to_enable_access')
                        : ''
                    }
                  >
                    <span>
                      <SmallButton
                        color="inherit"
                        data-test-id="projects-new-quote-cta"
                        disabled={isCompanySuspended}
                        onClick={handleCreateQuoteClick}
                        variant="outlined"
                      >
                        {t('project.new_quote')}
                      </SmallButton>
                    </span>
                  </Tooltip>
                  <Tooltip
                    title={
                      isCompanySuspended
                        ? t('billing.contact_billing_to_enable_access')
                        : ''
                    }
                  >
                    <span>
                      <SmallButton
                        data-test-id="projects-new-project-cta"
                        disabled={isCompanySuspended}
                        onClick={handleCreateProjectClick}
                      >
                        {t('project.new_project')}
                      </SmallButton>
                    </span>
                  </Tooltip>
                </>
              )}
            </Box>
          </Box>
        </GridRow>

        <GridRow>
          <Box sx={{ alignItems: 'center', display: 'flex', gap: 1 }}>
            <StatusTabs
              onChange={handleStateTabChange}
              selectedTab={stateTab}
              tabs={PROJECT_STATE_TABS_BY_VIEW[view].map((state) => ({
                label: getProjectStateLabel(state),
                value: state,
              }))}
            />

            <Box sx={{ minWidth: '360px', flex: 0 }}>
              <Search />
            </Box>

            <ProjectsFilters />
          </Box>
        </GridRow>

        <GridRow sx={{ bgcolor: 'transparent', p: 0, overflow: 'auto' }}>
          <Virtuoso
            data={Array.from({ length: itemCount }, (_, index) => index)}
            endReached={handleLoadMore}
            itemContent={renderProjectItem}
            overscan={5}
            style={{ height: '100%' }}
          />
        </GridRow>
      </GridContainer>
      <ProjectDialog
        creationMode={projectFormDialog.creationMode}
        isCopyAndEdit={projectFormDialog.isCopyAndEdit}
        isOpen={projectFormDialog.isOpen}
        onClose={() => setProjectFormDialog({ isOpen: false })}
        project={projectFormDialog.project}
      />
      <EmailQuoteDialog
        companyId={companyId}
        isOpen={emailQuoteDialog.isOpen}
        onClose={handleEmailQuoteClose}
        onSend={handleEmailQuoteSend}
        projectIds={emailQuoteDialog.project ? [emailQuoteDialog.project.id] : []}
      />
      <ConfirmationDialog
        isBusy={deleteProjectDialogState.isDeleting}
        isOpen={deleteProjectDialogState.isOpen}
        onClose={() => {
          setDeleteProjectDialogState((state) => ({
            ...state,
            isDeleting: false,
            isOpen: false,
          }));
        }}
        onConfirm={deleteProject}
        title={t('project.delete_project_title')}
      >
        <Typography>
          {t('project.delete_project_text', {
            name: deleteProjectDialogState.project?.name,
            interpolation: { escapeValue: false },
          })}
        </Typography>
      </ConfirmationDialog>
    </>
  );
}

export default observer(ProjectsContainer);
